import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, CheckCircle, FileText as Scroll } from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";
import { toHtml, sanitize } from "@/lib/richtext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse embedded PDF page divs for Forms & Letters — same shape as PlacementEditor.
function parsePages(html) {
  if (typeof window === "undefined" || !html) return [];
  const doc = new window.DOMParser().parseFromString(html, "text/html");
  return Array.from(doc.querySelectorAll(".pdf-form-page")).map((el) => {
    const style = el.getAttribute("style") || "";
    const wm = style.match(/width:\s*([\d.]+)pt/);
    const hm = style.match(/height:\s*([\d.]+)pt/);
    const img = el.querySelector("img");
    return {
      width: wm ? parseFloat(wm[1]) : 595,
      height: hm ? parseFloat(hm[1]) : 842,
      src: (img && img.getAttribute("src")) || "",
    };
  });
}

export default function PublicSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [typedName, setTypedName] = useState("");
  const [sig, setSig] = useState(null);
  const [sigMode, setSigMode] = useState("draw"); // "draw" | "upload" | "saved"
  const [savedSig, setSavedSig] = useState(null); // { dataUrl, name, savedAt } from localStorage
  const [saveForNext, setSaveForNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const highlightRef = useRef(null);

  // Load any saved signature from this device's localStorage
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("cosec_saved_sig");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.dataUrl === "string" && parsed.dataUrl.startsWith("data:image/")) {
          setSavedSig(parsed);
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    axios.get(`${API}/sign/${token}`)
      .then(r => {
        setData(r.data);
        setTypedName(r.data.signatory.name || "");
        if (r.data.signatory.signed_at) setDone(true);
      })
      .catch(e => setError(e.response?.data?.detail || "Invalid link"));
  }, [token]);

  const submit = async () => {
    if (!sig) return toast.error("Please provide your signature");
    if (!typedName.trim()) return toast.error("Please type your full name");
    setSubmitting(true);
    try {
      await axios.post(`${API}/sign/${token}`, { typed_name: typedName.trim(), signature_data_url: sig });
      if (saveForNext) {
        try {
          window.localStorage.setItem("cosec_saved_sig", JSON.stringify({
            dataUrl: sig,
            name: typedName.trim(),
            savedAt: new Date().toISOString(),
          }));
        } catch {}
      }
      toast.success("Signature recorded");
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sign");
    } finally { setSubmitting(false); }
  };

  const useSavedSignature = () => {
    if (!savedSig) return;
    setSig(savedSig.dataUrl);
    setSigMode("saved");
    if (savedSig.name && !typedName) setTypedName(savedSig.name);
  };
  const forgetSavedSignature = () => {
    try { window.localStorage.removeItem("cosec_saved_sig"); } catch {}
    setSavedSig(null);
    if (sigMode === "saved") { setSig(null); setSigMode("draw"); }
  };

  const pages = useMemo(() => parsePages(data?.resolution?.body || ""), [data]);
  const isFormOrLetter = (data?.category || "") === "form" || (data?.category || "") === "letter";
  const placement = data?.signatory?.placement || null;

  // After render, scroll the highlighted placement into view so signers see it.
  useEffect(() => {
    if (highlightRef.current) {
      try { highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }
  }, [placement, pages.length]);

  if (error) return <Shell><div className="p-10 text-center"><h1 className="text-2xl">Link invalid or expired</h1><p className="mt-2 text-muted-foreground">{error}</p></div></Shell>;
  if (!data) return <Shell><div className="p-10 text-sm text-muted-foreground">Loading…</div></Shell>;

  const { resolution, signatory, client, all_signatories } = data;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto p-6 md:p-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        <article className="border border-border bg-white">
          <header className="px-8 py-6 border-b border-border">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              {isFormOrLetter
                ? (data.category === "letter" ? "Letter" : "Form")
                : (resolution.resolution_type === "director" ? "Directors' Resolution" : "Members' Resolution")}
            </div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">{resolution.title}</h1>
            <div className="mt-2 font-mono text-xs text-muted-foreground">
              {client.name} · SSM {client.ssm_no}
            </div>
            {isFormOrLetter && placement && !done && (
              <div className="mt-3 inline-block border border-[#0F62FE] bg-[#0F62FE]/5 px-3 py-1 text-xs font-mono uppercase text-[#0F62FE]">
                Your signature will appear on Page {placement.page + 1} — highlighted below
              </div>
            )}
          </header>

          {isFormOrLetter && pages.length > 0 ? (
            <PdfPagesPreview
              pages={pages}
              placement={placement}
              done={done}
              signatureDataUrl={signatory.signature_data_url}
              highlightRef={highlightRef}
            />
          ) : (
            <div className="px-8 py-6 resolution-preview" data-testid="resolution-body"
                 dangerouslySetInnerHTML={{ __html: sanitize(toHtml(resolution.body)) }} />
          )}
        </article>

        <aside className="space-y-6">
          <section className="border border-border p-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">You are signing as</div>
            <div className="text-lg font-medium">{signatory.name}</div>
            {signatory.role && <div className="text-xs text-muted-foreground">{signatory.role}</div>}
            {isFormOrLetter && !placement && !done && (
              <div className="mt-3 border border-[#F1C21B] bg-[#F1C21B]/10 px-3 py-2 text-[11px] font-mono uppercase text-[#8A6D00]">
                No signature spot has been placed for you yet — the drafter needs to set one first.
              </div>
            )}

            {done ? (
              <div className="mt-6 border border-[#24A148]/40 bg-[#24A148]/5 p-4">
                <div className="flex items-center gap-2 text-[#24A148] font-medium">
                  <CheckCircle size={20} weight="fill" /> Signed
                </div>
                <div className="mt-2 text-xs text-muted-foreground font-mono">
                  Signed at {signatory.signed_at || "just now"}
                </div>
                {signatory.signature_data_url && (
                  <img src={signatory.signature_data_url} alt="signature" className="mt-3 border border-border bg-white p-2 max-w-full" />
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Type your full name</Label>
                  <Input className="mt-1 rounded-sm" value={typedName} onChange={e => setTypedName(e.target.value)}
                         data-testid="typed-name-input" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Signature</Label>
                  {savedSig && sigMode !== "saved" && (
                    <div className="mt-1 mb-2 border border-[#0F62FE]/40 bg-[#0F62FE]/[0.04] p-3 flex items-center gap-3"
                         data-testid="saved-sig-banner">
                      <img src={savedSig.dataUrl} alt="saved signature"
                           className="h-10 max-w-[120px] object-contain border border-border bg-white" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#0F62FE]">Saved on this device</div>
                        <div className="text-xs text-muted-foreground truncate">Use your last signature to sign in one click.</div>
                      </div>
                      <button type="button" onClick={useSavedSignature}
                              className="px-3 py-1.5 rounded-sm bg-[#0F62FE] text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#0043CE]"
                              data-testid="use-saved-sig-btn">Use</button>
                      <button type="button" onClick={forgetSavedSignature}
                              className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground underline"
                              data-testid="forget-saved-sig-btn">Forget</button>
                    </div>
                  )}
                  <div className="mt-1 mb-2 inline-flex border border-border rounded-sm overflow-hidden text-[11px] font-mono uppercase tracking-widest">
                    <button type="button"
                            onClick={() => { setSigMode("draw"); setSig(null); }}
                            className={`px-3 py-1.5 ${sigMode === "draw" ? "bg-[#0F62FE] text-white" : "bg-white text-muted-foreground"}`}
                            data-testid="sig-mode-draw">Draw</button>
                    <button type="button"
                            onClick={() => { setSigMode("upload"); setSig(null); }}
                            className={`px-3 py-1.5 border-l border-border ${sigMode === "upload" ? "bg-[#0F62FE] text-white" : "bg-white text-muted-foreground"}`}
                            data-testid="sig-mode-upload">Upload</button>
                    {savedSig && (
                      <button type="button"
                              onClick={useSavedSignature}
                              className={`px-3 py-1.5 border-l border-border ${sigMode === "saved" ? "bg-[#0F62FE] text-white" : "bg-white text-muted-foreground"}`}
                              data-testid="sig-mode-saved">Saved</button>
                    )}
                  </div>
                  {sigMode === "draw" && <SignaturePad onChange={setSig} />}
                  {sigMode === "upload" && <SignatureUploader onChange={setSig} value={sig} />}
                  {sigMode === "saved" && sig && (
                    <div className="border border-dashed border-[#0F62FE]/40 rounded-sm bg-white flex items-center justify-center p-4"
                         style={{ minHeight: 180 }}>
                      <img src={sig} alt="saved signature preview" className="max-h-40 max-w-full object-contain" />
                    </div>
                  )}
                  <label className="mt-2 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={saveForNext}
                           onChange={(e) => setSaveForNext(e.target.checked)}
                           data-testid="save-sig-checkbox"
                           className="accent-[#0F62FE]" />
                    Save this signature on my device for next time
                  </label>
                </div>
                <Button onClick={submit} disabled={submitting || (isFormOrLetter && !placement)}
                        className="w-full h-11 rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" data-testid="submit-sign-btn">
                  {submitting ? "Signing…" : "Sign electronically"}
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  By clicking sign, you agree that your electronic signature is the legal
                  equivalent of your manual signature and is bound by this document.
                </p>
              </div>
            )}
          </section>

          <section className="border border-border">
            <header className="px-6 py-3 border-b border-border font-mono text-[10px] uppercase tracking-widest">Signatories</header>
            {all_signatories.map((s, i) => (
              <div key={i} className="px-6 py-2 flex items-center justify-between border-b last:border-b-0 border-border text-sm">
                <div>
                  <div>{s.name}</div>
                  <div className="text-[11px] text-muted-foreground">{s.role}</div>
                </div>
                <div className={`text-[10px] font-mono uppercase ${s.signed ? "text-[#24A148]" : "text-muted-foreground"}`}>
                  {s.signed ? "Signed" : "Pending"}
                </div>
              </div>
            ))}
          </section>
        </aside>
      </div>
    </Shell>
  );
}

function PdfPagesPreview({ pages, placement, done, signatureDataUrl, highlightRef }) {
  const maxDisplayWidth = 720;
  const scale = Math.min(1, maxDisplayWidth / Math.max(...pages.map((p) => p.width)));
  return (
    <div className="p-6 bg-[#F3F4F6] space-y-4">
      {pages.map((p, pi) => {
        const show = placement && placement.page === pi;
        return (
          <div key={pi} className="relative mx-auto shadow-sm bg-white"
               style={{ width: p.width * scale, height: p.height * scale }}>
            <img src={p.src} alt={`Page ${pi + 1}`} draggable="false"
                 style={{ width: "100%", height: "100%", display: "block",
                          pointerEvents: "none", userSelect: "none" }} />
            {show && (
              <div ref={highlightRef}
                   style={{
                     position: "absolute",
                     left: placement.x * scale,
                     top: placement.y * scale,
                     width: placement.w * scale,
                     height: placement.h * scale,
                     border: "2px dashed #0F62FE",
                     background: done ? "transparent" : "rgba(15,98,254,0.12)",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     boxSizing: "border-box",
                     animation: done ? "none" : "pulse 1.6s ease-in-out infinite",
                   }}
                   data-testid="sign-here-highlight">
                {done && signatureDataUrl ? (
                  <img src={signatureDataUrl} alt="signature"
                       style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#0F62FE] bg-white/80 px-2 py-0.5">
                    Sign here
                  </span>
                )}
              </div>
            )}
            <div className="absolute top-1 right-1 font-mono text-[10px] uppercase text-muted-foreground bg-white/70 px-1">
              Page {pi + 1}
            </div>
          </div>
        );
      })}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.55}}`}</style>
    </div>
  );
}

function SignatureUploader({ onChange, value }) {
  const ref = useRef(null);
  const [name, setName] = useState("");
  const [processing, setProcessing] = useState(false);

  // Client-side background removal: turns near-white pixels transparent and
  // softens anti-aliased edges. Works well for scans / photos of signatures
  // on paper that come with a white background.
  const removeWhiteBackground = (imgSrc) => new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = data.data;
        const HARD = 235; // above this → fully transparent
        const SOFT = 170; // between SOFT and HARD → proportional alpha
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i], g = px[i + 1], b = px[i + 2];
          const minCh = Math.min(r, g, b);
          if (minCh >= HARD) {
            px[i + 3] = 0;
          } else if (minCh >= SOFT) {
            // Feather anti-aliased edges: scale alpha by how far below HARD
            const t = (HARD - minCh) / (HARD - SOFT); // 0 (near white) → 1 (near ink)
            px[i + 3] = Math.round(px[i + 3] * t);
          }
        }
        ctx.putImageData(data, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        resolve(imgSrc); // fallback to original on failure
      }
    };
    img.onerror = () => resolve(imgSrc);
    img.src = imgSrc;
  });

  const pick = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a PNG or JPG image");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image is too large (max 4 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setProcessing(true);
      const original = String(reader.result || "");
      const cleaned = await removeWhiteBackground(original);
      onChange?.(cleaned);
      setName(file.name);
      setProcessing(false);
    };
    reader.readAsDataURL(file);
  };
  const clear = () => { onChange?.(null); setName(""); if (ref.current) ref.current.value = ""; };
  return (
    <div>
      <div className="border border-dashed border-border rounded-sm flex flex-col items-center justify-center p-4"
           style={{
             minHeight: 180,
             backgroundImage: "linear-gradient(45deg, #F3F4F6 25%, transparent 25%), linear-gradient(-45deg, #F3F4F6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F3F4F6 75%), linear-gradient(-45deg, transparent 75%, #F3F4F6 75%)",
             backgroundSize: "12px 12px",
             backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0",
             backgroundColor: "#fff",
           }}>
        {processing ? (
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground text-center">
            Removing background…
          </div>
        ) : value ? (
          <img src={value} alt="uploaded signature" className="max-h-40 max-w-full object-contain" />
        ) : (
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground text-center">
            PNG or JPG · white background is auto-removed
          </div>
        )}
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg" hidden
             onChange={(e) => pick(e.target.files?.[0])}
             data-testid="signature-upload-input" />
      <div className="mt-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
        <button type="button" onClick={() => ref.current?.click()} className="underline"
                data-testid="signature-upload-choose">
          {value ? "Replace image" : "Choose image"}
        </button>
        <span className="truncate max-w-[160px] normal-case tracking-normal">{name}</span>
        {value && <button type="button" onClick={clear} className="underline"
                          data-testid="signature-upload-clear">Clear</button>}
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center gap-3">
          <Buildings size={20} weight="duotone" />
          <span className="font-mono uppercase tracking-[0.2em] text-xs">Specfield Management Services Sdn. Bhd.</span>
          <span className="ml-auto text-[11px] font-mono text-muted-foreground flex items-center gap-1">
            <Scroll size={14} weight="duotone" /> E-signing portal
          </span>
        </div>
      </header>
      {children}
    </div>
  );
}
